/**
 * Set or change the next event on a lead or a customer.
 *
 * The next event is not a new record type — it is the earliest open task on
 * the lead. Building a parallel entity would have produced two competing
 * to-do lists and two reminder jobs; this writes a LeadTask, which is what
 * My Day, the customer task panel and the manager's "no next step" exception
 * all read from.
 *
 * Follows the in-repo modal idiom: backdrop click closes, form click stops
 * propagation, submit on the <form>, `busy` while in flight, toast either way.
 */

import { useEffect, useMemo, useState } from 'react';
import { tasksService } from '../lib/services';
import { useToast } from '../lib/toast';
import { useConsultantOptions } from '../lib/useDirectory';
import LookupField from './LookupField';
import {
  orgWallClockToISO,
  orgTimezoneAbbrev,
  partsInOrgTz,
  civilToISODate,
  orgCivilToday,
  orgAddDays,
} from '../lib/datetime';

const PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];

const REMINDER_OFFSETS = [
  { value: '0',  label: 'At the due time' },
  { value: '15', label: '15 minutes before' },
  { value: '60', label: '1 hour before' },
  { value: '',   label: "Don't remind me" },
];

/** Tomorrow 09:00 in the org zone — a sensible default for "call them back". */
function defaultDueLocal() {
  const civil = orgAddDays(orgCivilToday(), 1);
  return `${civilToISODate(civil)}T09:00`;
}

/**
 * @param open       boolean
 * @param leadId     one of leadId / customerId is required
 * @param customerId
 * @param task       existing task to edit (omit to create)
 * @param defaultAssigneeId  usually the lead's assigned consultant
 * @param prefill    { title, reasonCode } — e.g. from nextBestAction()
 * @param onClose    () => void
 * @param onSaved    (task) => void
 */
export default function NextEventModal({
  open,
  leadId = null,
  customerId = null,
  task = null,
  defaultAssigneeId = null,
  prefill = null,
  onClose,
  onSaved,
}) {
  const { toast } = useToast();
  const { options: consultantOptions, loading: consultantsLoading, error: consultantsError } =
    useConsultantOptions();

  const [reasons, setReasons] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Reset only on open. Adding onClose to these deps would blank a
  // half-completed form every time the parent re-renders — LeadDetail polls
  // every 3 seconds, so that is not hypothetical.
  useEffect(() => {
    if (!open) return;
    setForm({
      reasonCode: task?.reasonCode || prefill?.reasonCode || '',
      title: task?.title || prefill?.title || '',
      dueLocal: task?.dueAt ? isoToLocalInput(task.dueAt) : defaultDueLocal(),
      remindOffset: '0',
      assigneeId: task?.assigneeId || defaultAssigneeId || '',
      priority: task?.priority || 'NORMAL',
      notes: task?.notes || '',
      type: task?.type || 'CALL',
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    tasksService
      .reasons({ appliesTo: leadId ? 'LEAD' : 'CUSTOMER' })
      .then((rows) => { if (!cancelled) setReasons(Array.isArray(rows) ? rows : []); })
      // A missing reason list must not block setting a next step — the field
      // just degrades to free text.
      .catch(() => { if (!cancelled) setReasons([]); });
    return () => { cancelled = true; };
  }, [open, leadId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const reason = useMemo(
    () => reasons.find((r) => r.code === form?.reasonCode) || null,
    [reasons, form?.reasonCode],
  );

  if (!open || !form) return null;

  const pickReason = (code) => {
    const r = reasons.find((x) => x.code === code);
    setForm((f) => ({
      ...f,
      reasonCode: code,
      // Only auto-fill an untouched title, never overwrite what was typed.
      title: f.title && f.title !== labelOf(reasons, f.reasonCode) ? f.title : (r?.label || ''),
      type: r?.defaultType || f.type,
    }));
  };

  const canSubmit = !busy && form.title.trim() && form.dueLocal && form.assigneeId;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      // orgWallClockToISO, never new Date(...).toISOString(): the input reads
      // as dealership wall-clock time, and a consultant working from another
      // zone would otherwise set the reminder to the wrong hour.
      const dueAt = orgWallClockToISO(form.dueLocal);
      const offset = form.remindOffset === '' ? null : Number(form.remindOffset);
      const remindAt = offset == null
        ? null
        : new Date(new Date(dueAt).getTime() - offset * 60_000).toISOString();

      const body = {
        title: form.title.trim(),
        type: form.type,
        priority: form.priority,
        reasonCode: form.reasonCode || null,
        dueAt,
        remindAt,
        notes: form.notes.trim() || null,
        assigneeId: form.assigneeId || null,
        leadId: leadId || null,
        customerId: customerId || null,
      };

      const saved = task
        ? await tasksService.update(task.id, body)
        : await tasksService.create(body);

      toast(task ? 'Next step updated' : 'Next step set', { variant: 'success' });
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      toast(`Could not save: ${err?.response?.data?.message || err?.message || 'unknown error'}`,
        { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <form
        className="modal"
        style={{ width: 'min(560px, 94vw)' }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="modal-header">
          <div className="modal-title">{task ? 'Change next step' : 'Set the next step'}</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="form-field">
            <label htmlFor="ne-reason">Reason</label>
            <select
              id="ne-reason"
              value={form.reasonCode}
              onChange={(e) => pickReason(e.target.value)}
            >
              <option value="">— pick a reason —</option>
              {reasons.map((r) => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="ne-title">What needs to happen *</label>
            <input
              id="ne-title"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="Call the customer to book a test drive"
              autoFocus
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-field">
              <label htmlFor="ne-due">When * <span className="text-muted">({orgTimezoneAbbrev()})</span></label>
              <input
                id="ne-due"
                type="datetime-local"
                value={form.dueLocal}
                onChange={(e) => set('dueLocal', e.target.value)}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="ne-remind">Remind me</label>
              <select
                id="ne-remind"
                value={form.remindOffset}
                onChange={(e) => set('remindOffset', e.target.value)}
              >
                {REMINDER_OFFSETS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <LookupField
            id="ne-assignee"
            label="Owner *"
            placeholder="Search consultants…"
            emptyHint="No consultants found"
            options={consultantOptions}
            loading={consultantsLoading}
            error={consultantsError}
            value={form.assigneeId}
            onChange={(v) => set('assigneeId', v)}
          />

          <div className="form-field">
            <label htmlFor="ne-priority">Priority</label>
            <select
              id="ne-priority"
              value={form.priority}
              onChange={(e) => set('priority', e.target.value)}
            >
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="ne-notes">Notes</label>
            <textarea
              id="ne-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Anything the owner should know"
            />
          </div>

          {reason?.convertsToAppointment && (
            <div className="mono text-xs text-muted">
              When you complete this, you'll be offered the booking straight away.
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={!canSubmit}>
            {busy ? 'Saving…' : (task ? 'Save' : 'Set next step')}
          </button>
        </div>
      </form>
    </div>
  );
}

function labelOf(reasons, code) {
  return reasons.find((r) => r.code === code)?.label || '';
}

/**
 * UTC instant → the `datetime-local` value that reads correctly in the ORG
 * zone. Deliberately not the browser zone: the field is submitted back through
 * orgWallClockToISO, so reading and writing must use the same clock or every
 * edit would silently shift the time.
 */
function isoToLocalInput(iso) {
  const p = partsInOrgTz(iso);
  if (!p) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}
